/*
 * RouteChooser — Orbit's landing (redesign 6a, content per Strategy v3).
 * The first thing a user sees: three big, richly-framed cards so they decide
 * with full context, not a guess. Each card answers four things — what it
 * means · what you get · what happens next · price. No dossier, no input.
 *
 *   Solve a problem  → problem capture (L1, free)
 *   Map the business → company-wide scoping (L2, paid)
 *   Explore first    → /marketplace catalog (free)
 *
 * Cards run full-width across the stage and share one height. The detail
 * block (You get / Next) is clamped with a fade; "See full" expands it into
 * a scrollable box so a long card reads easily without growing the row.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Target, Layers, Compass, ArrowRight, ChevronDown } from 'lucide-react';

export type OrbitRoute = 'problem' | 'company' | 'explore';

interface ChoiceDef {
  route: OrbitRoute;
  title: string;
  price: string;          // short pill copy
  priceTone: 'free' | 'paid';
  means: string;          // what it means — the hook
  get: string;            // what you get
  next: string;           // what happens next
  cta: string;
  Icon: React.ComponentType<any>;
}

const CHOICES: ChoiceDef[] = [
  {
    route: 'problem',
    title: 'Solve a problem',
    price: 'Free to scope',
    priceTone: 'free',
    means: 'You already know the problem you want to solve.',
    get: 'A scoped solution — a PRD and a path to a built agent, or a ready marketplace / customized agent.',
    next: 'A focused set of questions about the problem, then solution options, then your PRD.',
    cta: 'Scope my problem',
    Icon: Target,
  },
  {
    route: 'company',
    title: 'Map the business',
    price: 'Paid',
    priceTone: 'paid',
    means: "You want AI in your organization but aren't sure where it fits — you want a real picture of where it helps, and in what order.",
    get: 'Deep, company-wide scoping: prioritized problems across departments, a roadmap, and PRDs for what to build.',
    next: 'A few questions about your size and complexity, then we recommend automated or human-led scoping — with the price — and begin.',
    cta: 'Map my business',
    Icon: Layers,
  },
  {
    route: 'explore',
    title: 'Just exploring',
    price: 'Free',
    priceTone: 'free',
    means: "You're here to look around and see what's possible.",
    get: 'Browse the marketplace, see real examples, generate demo PRDs.',
    next: "Explore freely — jump into either path whenever you're ready, and your details carry over.",
    cta: 'Start exploring',
    Icon: Compass,
  },
];

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="aa-route-card-row">
    <span className="aa-route-card-row-label">{label}</span>
    <span className="aa-route-card-row-val">{children}</span>
  </div>
);

const RouteCard: React.FC<{ def: ChoiceDef; disabled?: boolean; onPick: (r: OrbitRoute) => void }> = ({ def, disabled, onPick }) => {
  const { route, title, price, priceTone, means, get, next, cta, Icon } = def;
  const detailRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Measure once collapsed: does the detail block exceed the clamp height?
  // (When expanded the box scrolls, so we keep the toggle regardless.)
  useLayoutEffect(() => {
    const el = detailRef.current;
    if (!el) return;
    const measure = () => {
      if (!expanded) setOverflowing(el.scrollHeight > el.clientHeight + 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded]);

  const pick = () => { if (!disabled) onPick(route); };
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(v => !v);
  };
  const showToggle = overflowing || expanded;

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={`aa-route-card${disabled ? ' is-disabled' : ''}`}
      onClick={pick}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) { e.preventDefault(); pick(); } }}
    >
      <div className="aa-route-card-head">
        <span className="aa-route-card-icon"><Icon size={20} strokeWidth={2} /></span>
        <span className={`aa-route-card-price aa-route-card-price--${priceTone}`}>{price}</span>
      </div>
      <span className="aa-route-card-title">{title}</span>
      <span className="aa-route-card-means">{means}</span>

      <div
        ref={detailRef}
        className={`aa-route-card-detail${expanded ? ' is-open' : ''}${showToggle && !expanded ? ' is-faded' : ''}`}
      >
        <DetailRow label="You get">{get}</DetailRow>
        <DetailRow label="Next">{next}</DetailRow>
      </div>

      {showToggle && (
        <button type="button" className="aa-route-card-more" onClick={toggle}>
          {expanded ? 'Show less' : 'See full'}
          <ChevronDown size={13} strokeWidth={2.5} className={expanded ? 'is-flipped' : ''} />
        </button>
      )}

      <span className="aa-route-card-cta">{cta}<ArrowRight size={15} strokeWidth={2.5} /></span>
    </div>
  );
};

export const RouteChooser: React.FC<{ onPick: (route: OrbitRoute) => void; disabled?: boolean }> = ({ onPick, disabled }) => {
  return (
    <div className="aa-route-chooser">
      <div className="aa-route-chooser-lead">
        <span className="aa-route-chooser-eyebrow">Welcome to Orbit</span>
        <h2 className="aa-route-chooser-title">
          What brings you in <span className="aa-route-chooser-em">today</span>?
        </h2>
        <p className="aa-route-chooser-sub">
          Pick one — I'll scope it with you before any code gets written. You can change course any time, and what you tell me carries over.
        </p>
      </div>
      <div className="aa-route-chooser-grid">
        {CHOICES.map((def) => (
          <RouteCard key={def.route} def={def} disabled={disabled} onPick={onPick} />
        ))}
      </div>
    </div>
  );
};

export default RouteChooser;
